import React from "react";

import {
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    TextField,
} from "@mui/material";

import { Source } from "Constant/source";

import { sourceGoogleSheets } from "helpers/Sources/googleSheets";

import LoginButton from "components/molecules/LoginButton";
import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";
import GenericTable from "components/organisms/GenericTable";

import { SPREADSHEET_PAGE_SIZE_OPTIONS } from "../../SourceCreation.constants";

const SpreadsheetSourceForm = ({
    language,
    spreadsheet,
    registerFooterAction,
}) => {
    const {
        domain,
        setDomain,
        isLoginVisible,
        isAuthLoading,
        setIsAuthLoading,
        isLoading,
        isCreating,
        hasSpreadsheets,
        spreadsheets,
        selectedSpreadsheetId,
        rowsPerPage,
        setRowsPerPage,
        hasPages,
        pageOptions,
        isPagesLoading,
        selectedPages,
        handlePagesChange,
        handleLoginSuccess,
        handleSpreadsheetRowClick,
        createSource,
    } = spreadsheet;

    const selectPagesLabel =
        language?.source_modal?.select_pages ?? "Select pages...";

    // "Connect with Google" stays in the content; only the final create
    // action is rendered by the modal footer once pages are available.
    useHostFooterAction(registerFooterAction, "form", {
        label: language?.buttons?.create_source ?? "Create Source",
        onSubmit: createSource,
        isDisabled: !selectedPages.length || isCreating,
        isLoading: isCreating,
        hidden: !hasPages,
    });

    return (
        <Box>
            <FormControl fullWidth sx={{ mb: 3 }}>
                <TextField
                    label={language?.source_modal?.domain ?? "Domain"}
                    variant="outlined"
                    value={domain}
                    onChange={(event) => setDomain(event.target.value)}
                    placeholder="example.com"
                    className="cloud-domain-input"
                />
            </FormControl>

            {hasSpreadsheets && spreadsheets && (
                <Box sx={{ maxWidth: "100%", minWidth: 0, width: "100%" }}>
                    <GenericTable
                        columns={[
                            {
                                key: "name",
                                header: "Name",
                                render: (row) => row.name,
                            },
                        ]}
                        rows={spreadsheets}
                        getRowKey={(row) => row.id}
                        pageSize={rowsPerPage}
                        onPageSizeChange={setRowsPerPage}
                        pageSizeOptions={SPREADSHEET_PAGE_SIZE_OPTIONS}
                        autoScroll={false}
                        onRowClick={(row) => handleSpreadsheetRowClick(row.id)}
                        isRowSelected={(row) =>
                            selectedSpreadsheetId === row.id
                        }
                    />
                </Box>
            )}

            {(isPagesLoading || pageOptions.length > 0) && (
                <FormControl fullWidth sx={{ my: 3 }}>
                    <Autocomplete
                        className="cloud-google-pages-select"
                        onChange={handlePagesChange}
                        value={selectedPages}
                        loading={isPagesLoading}
                        disabled={isPagesLoading}
                        multiple
                        options={pageOptions}
                        getOptionLabel={(option) => option.name}
                        freeSolo
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={selectPagesLabel}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isPagesLoading && (
                                                <CircularProgress
                                                    color="inherit"
                                                    size={20}
                                                />
                                            )}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </FormControl>
            )}

            {isLoginVisible && (
                <LoginButton
                    code="google-sheets"
                    getAuthUrl={sourceGoogleSheets}
                    getAuthUrlData={{ type_name: "google_spreadsheet" }}
                    getAuthUrlResponseTransform={(response) =>
                        response?.data?.url
                    }
                    paramNames={["code"]}
                    onSuccess={handleLoginSuccess}
                    dataTransform={(params) => ({
                        google_code: params.code,
                        source_type: Source.spreadsheet,
                        domain,
                    })}
                    onLoadingChange={setIsAuthLoading}
                    logoSrc="/assets/images/google.png"
                    buttonText={
                        language?.buttons?.connect_with_google ??
                        "Connect with Google"
                    }
                    disabled={!domain?.length}
                    isLoadingExternal={isAuthLoading || isLoading}
                />
            )}
        </Box>
    );
};

export default SpreadsheetSourceForm;
